import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  DocumentTextIcon,
  ClockIcon,
  UserMinusIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  TicketIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  MagnifyingGlassCircleIcon,
  FireIcon,
  PauseCircleIcon,
  DocumentCheckIcon,
  CogIcon,
  ShieldCheckIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import ContactFilteredTicketsTable from './components/ContactFilteredTicketsTable';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

interface ContactStats {
  // Row 1 - Cases
  totalActiveCases: number;
  awaitingInfoCases: number;
  unassignedCases: number;
  closedCases: number;
  // Row 2 - Incidents
  activeIncidents: number;
  criticalMajorIncidents: number;
  incidentsOnHold: number;
  closedIncidents: number;
  // Row 3 - Changes & Service Requests
  activeChanges: number;
  changesAwaitingApproval: number;
  activeServiceRequests: number;
  closedChanges: number;
  closedServiceRequests: number;
  // Row 4 - Problems
  activeProblems: number;
  problemsAwaitingReview: number;
  problemsAwaitingApproval: number;
  closedProblems: number;
}

interface StatCardProps {
  value: number | string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  iconColor?: string;
  iconBgColor?: string;
  onClick?: () => void;
  suffix?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  value,
  label,
  icon: Icon,
  iconColor = 'text-gray-500',
  iconBgColor = 'bg-gray-100',
  onClick,
  suffix
}) => (
  <div
    className={`bg-white rounded-lg shadow p-3 hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="text-2xl font-bold text-gray-900">
          {value}{suffix && <span className="text-sm ml-1">{suffix}</span>}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">{label}</div>
      </div>
      <div className={`p-1.5 rounded-lg ${iconBgColor}`}>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
    </div>
  </div>
);

const ContactDashboard: React.FC = () => {
  const { user } = useAuth();

  // Table visibility state
  const [tableVisible, setTableVisible] = useState(false);
  const [tableFilterLabel, setTableFilterLabel] = useState('');
  const [tableFilterType, setTableFilterType] = useState('');

  // Fetch dashboard stats
  const { data: stats, isLoading } = useQuery<ContactStats>({
    queryKey: ['contact-dashboard-stats', user?.id],
    queryFn: async () => {
      const response = await api.get('/dashboard/contact-stats');
      return response.data;
    },
    enabled: !!user?.id
  });

  // Click handlers for stat cards
  const handleStatClick = (filterType: string, label: string) => {
    setTableFilterType(filterType);
    setTableFilterLabel(label);
    setTableVisible(true);
  };

  const closeTable = () => {
    setTableVisible(false);
    setTableFilterType('');
    setTableFilterLabel('');
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  // Ticket Type Distribution Chart (Doughnut)
  const ticketTypeChartData = {
    labels: ['Cases', 'Incidents', 'Service Requests', 'Changes', 'Problems'],
    datasets: [
      {
        data: [
          stats?.totalActiveCases || 0,
          stats?.activeIncidents || 0,
          stats?.activeServiceRequests || 0,
          stats?.activeChanges || 0,
          stats?.activeProblems || 0
        ],
        backgroundColor: [
          'rgba(147, 51, 234, 0.8)',  // Purple for cases
          'rgba(239, 68, 68, 0.8)',   // Red for incidents
          'rgba(59, 130, 246, 0.8)',  // Blue for service requests
          'rgba(16, 185, 129, 0.8)',  // Green for changes
          'rgba(245, 158, 11, 0.8)'   // Amber for problems
        ],
        borderColor: [
          'rgb(147, 51, 234)',
          'rgb(239, 68, 68)',
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(245, 158, 11)'
        ],
        borderWidth: 2,
        hoverOffset: 8
      }
    ]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 15,
          usePointStyle: true,
          font: { size: 12 }
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = total > 0 ? ((context.raw / total) * 100).toFixed(1) : 0;
            return `${context.label}: ${context.raw} (${percentage}%)`;
          }
        }
      }
    }
  };

  // Status Distribution Chart (Bar) - Updated for new metrics
  const statusChartData = {
    labels: ['Active Cases', 'Awaiting Info', 'Unassigned', 'Critical/Major', 'Closed Cases'],
    datasets: [
      {
        label: 'Tickets',
        data: [
          stats?.totalActiveCases || 0,
          stats?.awaitingInfoCases || 0,
          stats?.unassignedCases || 0,
          stats?.criticalMajorIncidents || 0,
          stats?.closedCases || 0
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',   // Blue - Active
          'rgba(245, 158, 11, 0.8)',   // Amber - Awaiting Info
          'rgba(107, 114, 128, 0.8)',  // Gray - Unassigned
          'rgba(239, 68, 68, 0.8)',    // Red - Critical/Major
          'rgba(16, 185, 129, 0.8)'    // Green - Closed
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(245, 158, 11)',
          'rgb(107, 114, 128)',
          'rgb(239, 68, 68)',
          'rgb(16, 185, 129)'
        ],
        borderWidth: 1,
        borderRadius: 4
      }
    ]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.raw} tickets`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 } }
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { precision: 0 }
      }
    }
  };

  const totalActiveTickets = (stats?.totalActiveCases || 0) + (stats?.activeIncidents || 0) +
                            (stats?.activeServiceRequests || 0) + (stats?.activeChanges || 0) +
                            (stats?.activeProblems || 0);

  return (
    <div className="p-6">
      {/* Welcome section */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Here's an overview of your support tickets and service requests. Click on any tile to view details.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="space-y-4 mb-8">
        {/* Row 1 - Cases */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            value={stats?.totalActiveCases || 0}
            label="Total Active Cases"
            icon={DocumentTextIcon}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
            onClick={() => handleStatClick('totalActiveCases', 'Total Active Cases')}
          />
          <StatCard
            value={stats?.awaitingInfoCases || 0}
            label="Awaiting Info"
            icon={ClockIcon}
            iconColor="text-amber-600"
            iconBgColor="bg-amber-100"
            onClick={() => handleStatClick('awaitingInfoCases', 'Cases Awaiting Information')}
          />
          <StatCard
            value={stats?.unassignedCases || 0}
            label="Unassigned Cases"
            icon={UserMinusIcon}
            iconColor="text-gray-600"
            iconBgColor="bg-gray-100"
            onClick={() => handleStatClick('unassignedCases', 'Unassigned Cases')}
          />
          <StatCard
            value={stats?.closedCases || 0}
            label="Closed Cases"
            icon={CheckCircleIcon}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
            onClick={() => handleStatClick('closedCases', 'Closed Cases')}
          />
        </div>

        {/* Row 2 - Incidents */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            value={stats?.activeIncidents || 0}
            label="Active Incidents"
            icon={ExclamationTriangleIcon}
            iconColor="text-red-600"
            iconBgColor="bg-red-100"
            onClick={() => handleStatClick('activeIncidents', 'Active Incidents')}
          />
          <StatCard
            value={stats?.criticalMajorIncidents || 0}
            label="Critical/Major Incidents"
            icon={FireIcon}
            iconColor="text-red-700"
            iconBgColor="bg-red-200"
            onClick={() => handleStatClick('criticalMajorIncidents', 'Critical/Major Incidents')}
          />
          <StatCard
            value={stats?.incidentsOnHold || 0}
            label="Incidents on Hold"
            icon={PauseCircleIcon}
            iconColor="text-amber-600"
            iconBgColor="bg-amber-100"
            onClick={() => handleStatClick('incidentsOnHold', 'Incidents on Hold')}
          />
          <StatCard
            value={stats?.closedIncidents || 0}
            label="Closed Incidents"
            icon={DocumentCheckIcon}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
            onClick={() => handleStatClick('closedIncidents', 'Closed Incidents')}
          />
        </div>

        {/* Row 3 - Changes & Service Requests (5 tiles) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            value={stats?.activeChanges || 0}
            label="Active Changes"
            icon={ArrowPathIcon}
            iconColor="text-teal-600"
            iconBgColor="bg-teal-100"
            onClick={() => handleStatClick('activeChanges', 'Active Changes')}
          />
          <StatCard
            value={stats?.changesAwaitingApproval || 0}
            label="Changes Awaiting Approval"
            icon={ClipboardDocumentCheckIcon}
            iconColor="text-indigo-600"
            iconBgColor="bg-indigo-100"
            onClick={() => handleStatClick('changesAwaitingApproval', 'Changes Awaiting Approval')}
          />
          <StatCard
            value={stats?.closedChanges || 0}
            label="Closed Changes"
            icon={CogIcon}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
            onClick={() => handleStatClick('closedChanges', 'Closed Changes')}
          />
          <StatCard
            value={stats?.activeServiceRequests || 0}
            label="Active Service Requests"
            icon={TicketIcon}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
            onClick={() => handleStatClick('activeServiceRequests', 'Active Service Requests')}
          />
          <StatCard
            value={stats?.closedServiceRequests || 0}
            label="Closed SRs"
            icon={ShieldCheckIcon}
            iconColor="text-emerald-600"
            iconBgColor="bg-emerald-100"
            onClick={() => handleStatClick('closedServiceRequests', 'Closed Service Requests')}
          />
        </div>

        {/* Row 4 - Problems (4 tiles) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            value={stats?.activeProblems || 0}
            label="Active Problems"
            icon={WrenchScrewdriverIcon}
            iconColor="text-amber-600"
            iconBgColor="bg-amber-100"
            onClick={() => handleStatClick('activeProblems', 'Active Problems')}
          />
          <StatCard
            value={stats?.problemsAwaitingReview || 0}
            label="Problems Awaiting Review"
            icon={MagnifyingGlassCircleIcon}
            iconColor="text-orange-600"
            iconBgColor="bg-orange-100"
            onClick={() => handleStatClick('problemsAwaitingReview', 'Problems Awaiting Review')}
          />
          <StatCard
            value={stats?.problemsAwaitingApproval || 0}
            label="Problems Awaiting Approval"
            icon={ClipboardDocumentCheckIcon}
            iconColor="text-yellow-600"
            iconBgColor="bg-yellow-100"
            onClick={() => handleStatClick('problemsAwaitingApproval', 'Problems Awaiting Approval')}
          />
          <StatCard
            value={stats?.closedProblems || 0}
            label="Problems Closed"
            icon={XCircleIcon}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
            onClick={() => handleStatClick('closedProblems', 'Closed Problems')}
          />
        </div>
      </div>

      {/* Filtered Tickets Table */}
      <ContactFilteredTicketsTable
        visible={tableVisible}
        filterLabel={tableFilterLabel}
        filterType={tableFilterType}
        onClose={closeTable}
      />

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 mt-8">
        {/* Ticket Type Distribution - Doughnut Chart */}
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Active Tickets by Type</h3>
          <div className="h-64">
            {totalActiveTickets > 0 ? (
              <Doughnut data={ticketTypeChartData} options={doughnutOptions} />
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No active tickets
              </div>
            )}
          </div>
        </div>

        {/* Status Distribution - Bar Chart */}
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Ticket Status Overview</h3>
          <div className="h-64">
            <Bar data={statusChartData} options={barOptions} />
          </div>
        </div>
      </div>

    </div>
  );
};

export default ContactDashboard;
